/**
 * DashboardConfigList – Flat list of all saved dashboard configs.
 * Each card is clickable to view the dashboard, and has Edit/Delete actions.
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  LayoutDashboard,
  Star,
  BarChart2,
  SlidersHorizontal,
  CalendarDays,
  RefreshCw,
  Pencil,
  Trash2,
  ChevronRight,
} from 'lucide-react';
import type { NormalisedDashboardItem } from '@/types/dashboardConfig';

interface DashboardConfigListProps {
  items: NormalisedDashboardItem[];
  itemsLoading: boolean;
  deletingId: number | null;
  fetchingEditId?: number | null;
  onCardClick: (item: NormalisedDashboardItem) => void;
  onEdit: (item: NormalisedDashboardItem) => void;
  onDelete: (item: NormalisedDashboardItem) => void;
  compact?: boolean;
  selectedId?: number | null;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function ConfigCard({
  item,
  deletingId,
  fetchingEditId,
  isSelected,
  onCardClick,
  onEdit,
  onDelete,
}: {
  item: NormalisedDashboardItem;
  deletingId: number | null;
  fetchingEditId?: number | null;
  isSelected?: boolean;
  onCardClick: (item: NormalisedDashboardItem) => void;
  onEdit: (item: NormalisedDashboardItem) => void;
  onDelete: (item: NormalisedDashboardItem) => void;
}) {
  const isDeleting = deletingId === item.id;
  const isLoadingEdit = fetchingEditId === item.id;

  return (
    <Card
      className={`relative shadow-none hover:shadow-sm transition-all cursor-pointer group border ${
        isSelected
          ? 'bg-primary/10 border-primary'
          : 'bg-card border-border/60 hover:border-primary/40 hover:bg-accent/40'
      }`}
      onClick={() => onCardClick(item)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {/* Body */}
          <div className="min-w-0 flex-1 space-y-1">
            {/* Title row */}
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-semibold text-sm text-foreground truncate flex-1">
                {item.customName}
              </span>
              {item.isDefault && (
                <Badge className="shrink-0 gap-0.5 text-[9px] py-0 px-1 h-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
                  <Star className="h-2 w-2 fill-current" />
                  Default
                </Badge>
              )}
            </div>

            {/* Source name */}
            {item.metabaseQuestionName && item.metabaseQuestionName !== item.customName && (
              <p className="text-xs text-muted-foreground truncate">
                {item.metabaseQuestionName}
              </p>
            )}

            {/* Meta row — compact, single line */}
            <div className="flex items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground/80 flex-wrap">
              {item.dashboardQuestions.length > 0 && (
                <span
                  className="flex items-center gap-1"
                  title={`${item.dashboardQuestions.length} question${item.dashboardQuestions.length === 1 ? '' : 's'}`}
                >
                  <BarChart2 className="h-2.5 w-2.5" />
                  {item.dashboardQuestions.length}
                </span>
              )}
              {item.dashboardQuestions.length > 0 && item.dashboardFilters.length > 0 && (
                <span className="text-border">•</span>
              )}
              {item.dashboardFilters.length > 0 && (
                <span
                  className="flex items-center gap-1"
                  title={`${item.dashboardFilters.length} filter${item.dashboardFilters.length === 1 ? '' : 's'}`}
                >
                  <SlidersHorizontal className="h-2.5 w-2.5" />
                  {item.dashboardFilters.length}
                </span>
              )}
              {item.createdAt && (
                <>
                  {(item.dashboardQuestions.length > 0 || item.dashboardFilters.length > 0) && (
                    <span className="text-border">•</span>
                  )}
                  <span
                    className="flex items-center gap-1"
                    title={`Created: ${formatDate(item.createdAt)}`}
                  >
                    <CalendarDays className="h-2.5 w-2.5" />
                    {formatDate(item.createdAt)}
                  </span>
                </>
              )}
              {item.updatedDate && (
                <>
                  <span className="text-border">•</span>
                  <span
                    className="flex items-center gap-1"
                    title={`Updated: ${formatDate(item.updatedDate)}`}
                  >
                    <RefreshCw className="h-2.5 w-2.5" />
                    {formatDate(item.updatedDate)}
                  </span>
                </>
              )}
            </div>

            {/* Role chips */}
            {item.userTypes.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {item.userTypes.map((role) => (
                  <Badge
                    key={role}
                    variant="secondary"
                    className="text-[9px] px-1.5 py-0 h-4 font-medium"
                  >
                    {role}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Actions — show on hover */}
          <div className="flex items-center gap-0.5 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-accent"
              onClick={(e) => { e.stopPropagation(); onEdit(item); }}
              disabled={isDeleting || isLoadingEdit || fetchingEditId !== null}
              title="Edit"
            >
              {isLoadingEdit
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Pencil className="h-3 w-3" />
              }
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              onClick={(e) => { e.stopPropagation(); onDelete(item); }}
              disabled={isDeleting || fetchingEditId !== null}
              title="Delete"
            >
              {isDeleting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardConfigList({
  items,
  itemsLoading,
  deletingId,
  fetchingEditId,
  onCardClick,
  onEdit,
  onDelete,
  compact = false,
  selectedId = null,
}: DashboardConfigListProps) {
  const [confirmItem, setConfirmItem] = useState<NormalisedDashboardItem | null>(null);
  const [editWarningItem, setEditWarningItem] = useState<NormalisedDashboardItem | null>(null);

  const handleDeleteClick = (item: NormalisedDashboardItem) => {
    setConfirmItem(item);
  };

  const handleEditClick = (item: NormalisedDashboardItem) => {
    if (item.isDefault) {
      setEditWarningItem(item);
    } else {
      onEdit(item);
    }
  };

  const handleConfirmEdit = () => {
    if (editWarningItem) {
      onEdit(editWarningItem);
      setEditWarningItem(null);
    }
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    if (confirmItem) {
      onDelete(confirmItem);
      setConfirmItem(null);
    }
  };

  if (itemsLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-6 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading configs…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 -mt-8 text-center text-muted-foreground">
        <LayoutDashboard className="h-12 w-12 mb-3 opacity-25" />
        <p className="font-medium text-sm">No dashboard configs yet</p>
        <p className="text-xs mt-1">Click "Add Analytics Dashboard" to create.</p>
      </div>
    );
  }

  const sortedItems = [...items].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <>
      <div className={compact ? 'flex flex-col gap-3' : 'grid grid-cols-1 lg:grid-cols-2 gap-3'}>
        {sortedItems.map((item) => (
          <ConfigCard
            key={item.id}
            item={item}
            deletingId={deletingId}
            fetchingEditId={fetchingEditId}
            isSelected={selectedId === item.id}
            onCardClick={onCardClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        ))}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!confirmItem} onOpenChange={(v) => !v && setConfirmItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dashboard?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{' '}
              <span className="font-semibold text-foreground">
                "{confirmItem?.customName}"
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
              disabled={deletingId !== null}
            >
              {deletingId !== null ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit default dashboard warning dialog */}
      <AlertDialog open={!!editWarningItem} onOpenChange={(v) => !v && setEditWarningItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-yellow-600">
              ⚠️ Edit Default Dashboard
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">
                "{editWarningItem?.customName}"
              </span>{' '}
              is marked as the <strong>default dashboard</strong> and is visible to all users with the assigned role(s). Changes will take effect immediately for those users. Are you sure you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-yellow-600 text-white hover:bg-yellow-700"
              onClick={handleConfirmEdit}
            >
              Continue Editing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
